<template>
  <modal :show="show" :show-close="false" class="modal-scroll-fix white-content move-tools-modal" @update:show="$emit('update:show', $event)">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="card-title mb-0">{{ $t('toolTypes.moveTools') }} — {{ sourceToolTypeName }}</h5>
      <span class="modal-close-icon" @click="$emit('update:show', false)">
        <i class="material-icons">close</i>
      </span>
    </div>

    <!-- Select target tool type -->
    <div class="mb-3">
      <label class="form-label fw-bold">{{ $t('toolTypes.targetToolType') }}</label>
      <el-select
        v-model="selectedTargetToolTypeId"
        :placeholder="$t('toolTypes.selectTargetToolType')"
        filterable
        class="w-100"
      >
        <el-option
          v-for="tt in availableTargetToolTypes"
          :key="tt.id"
          :label="tt.type"
          :value="tt.id"
        />
      </el-select>
    </div>

    <!-- Move mode toggle -->
    <div class="mb-3">
      <label class="form-label fw-bold">{{ $t('toolTypes.selectWhichTools') }}</label>
      <NavPills
        :model-value="moveModeIndex"
        :items="moveModeItems"
        @update:model-value="onMoveModeChange"
      />
    </div>

    <!-- Tool selection table (shown when moveMode === 'selected') -->
    <div v-if="moveMode === 'selected'">
      <div v-if="loading" class="text-center py-3">
        <i class="material-icons spin">sync</i>
      </div>
      <div v-else-if="tools.length === 0" class="text-center py-3 text-muted">
        {{ $t('toolTypes.noToolsOfType') }}
      </div>
      <template v-if="!loading && tools.length > 0">
        <!-- Desktop table -->
        <el-table
          v-if="!isMobile"
          :data="filteredTools"
          @selection-change="handleSelectionChange"
          @row-click="toggleRowSelection"
          ref="toolTable"
          max-height="400"
        >
          <el-table-column type="selection" width="45" :selectable="canSelectTool" />
          <el-table-column :label="$t('common.name')" prop="name" min-width="150" />
          <el-table-column :label="$t('common.status')" min-width="140">
            <template #header>
              <el-select
                v-model="selectedStatusFilter"
                :class="selectClass"
                :placeholder="$t('common.status')"
                clearable
                @change="val => selectedStatusFilter = val"
              >
                <el-option
                  v-for="s in statusFilterOptions"
                  :key="s.id"
                  :label="s.status"
                  :value="s.id"
                />
              </el-select>
            </template>
            <template #default="{ row }">
              <el-tag v-if="row.isReserved" type="danger" size="small">
                {{ $t('locations.reserved') }}
              </el-tag>
              <el-tag v-else :type="statusTagType(row.toolStatusId)" size="small">
                {{ statusLabel(row.toolStatusId) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column :label="$t('tools.reservedBy')" min-width="180">
            <template #default="{ row }">
              <div v-if="row.reservedByName">
                <div>{{ row.reservedByName }}</div>
                <div v-if="row.reservedByPhone" class="text-muted small">
                  <a :href="'tel:' + row.reservedByPhone">{{ row.reservedByPhone }}</a>
                </div>
                <div v-if="row.reservedByEmail" class="text-muted small">
                  <a :href="'mailto:' + row.reservedByEmail">{{ row.reservedByEmail }}</a>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <!-- Mobile table -->
        <el-table
          v-else
          :data="filteredTools"
          @selection-change="handleSelectionChange"
          @row-click="toggleRowSelection"
          ref="toolTable"
          max-height="400"
        >
          <el-table-column type="selection" width="40" :selectable="canSelectTool" />
          <el-table-column :label="$t('common.info')" min-width="150">
            <template #default="{ row }">
              <div>
                <div><strong>{{ row.name }}</strong></div>
                <div>
                  <el-tag v-if="row.isReserved" type="danger" size="small">
                    {{ $t('locations.reserved') }}
                  </el-tag>
                  <el-tag v-else :type="statusTagType(row.toolStatusId)" size="small">
                    {{ statusLabel(row.toolStatusId) }}
                  </el-tag>
                </div>
                <div v-if="row.reservedByName" class="small mt-1">
                  <strong>{{ $t('tools.reservedBy') }}:</strong> {{ row.reservedByName }}
                  <span v-if="row.reservedByPhone"> · <a :href="'tel:' + row.reservedByPhone">{{ row.reservedByPhone }}</a></span>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </div>

    <!-- Summary when moveMode === 'all' -->
    <div v-if="moveMode === 'all' && !loading">
      <div v-if="tools.length === 0" class="text-center py-3 text-muted">
        {{ $t('toolTypes.noToolsOfType') }}
      </div>
      <div v-else class="summary-box">
        <div>{{ $t('toolTypes.totalToolsOfType') }}: <strong>{{ tools.length }}</strong></div>
        <div v-if="reservedCount > 0" class="mt-1">
          <el-tag type="danger" size="small">{{ reservedCount }} {{ $t('locations.reservedWillBeSkipped') }}</el-tag>
        </div>
      </div>
    </div>

    <!-- Delete after move option -->
    <div v-if="canDeleteAfterMove" class="mt-3">
      <el-checkbox v-model="deleteAfterMove">
        {{ $t('toolTypes.deleteToolTypeAfterMove') }}
      </el-checkbox>
    </div>

    <!-- Action buttons -->
    <div class="row mt-3">
      <div class="col-sm d-flex justify-content-end">
        <material-button
          :color="deleteAfterMove ? 'danger' : 'info'"
          fill
          :disabled="!canMove"
          @click="confirmMove"
        >
          {{ deleteAfterMove ? $t('toolTypes.moveAndDelete') : $t('toolTypes.moveTools') }}
        </material-button>
      </div>
    </div>
  </modal>
</template>

<script>
import { ElTable, ElTableColumn, ElSelect, ElOption, ElTag, ElCheckbox } from 'element-plus';
import Modal from './Modal.vue';
import MaterialButton from '../MaterialButton.vue';
import NavPills from '../NavPills.vue';
import api from '../../services/api';
import swal from 'sweetalert2';

export default {
  components: { Modal, MaterialButton, NavPills, ElTable, ElTableColumn, ElSelect, ElOption, ElTag, ElCheckbox },
  props: {
    show: { type: Boolean, required: true },
    sourceToolTypeId: { type: Number, required: true },
    sourceToolTypeName: { type: String, default: '' },
    toolTypes: { type: Array, required: true }
  },
  emits: ['update:show', 'tools-moved'],
  data() {
    return {
      selectedTargetToolTypeId: null,
      moveMode: 'all',
      tools: [],
      selectedTools: [],
      loading: false,
      deleteAfterMove: false,
      selectedStatusFilter: '',
      isMobile: window.innerWidth <= 1024
    };
  },
  mounted() {
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  computed: {
    moveModeItems() {
      return [
        { label: this.$t('locations.moveAllTools'), icon: 'select_all' },
        { label: this.$t('locations.moveSelectedTools'), icon: 'checklist' }
      ];
    },
    moveModeIndex() {
      return this.moveMode === 'all' ? 0 : 1;
    },
    availableTargetToolTypes() {
      return this.toolTypes.filter(tt => tt.id !== this.sourceToolTypeId);
    },
    reservedCount() {
      return this.tools.filter(t => t.isReserved).length;
    },
    movableAllCount() {
      return this.tools.filter(t => !t.isReserved).length;
    },
    movableToolCount() {
      if (this.moveMode === 'all') {
        return this.movableAllCount;
      }
      return this.selectedTools.length;
    },
    canMove() {
      return this.selectedTargetToolTypeId && this.movableToolCount > 0;
    },
    statusFilterOptions() {
      return [
        { id: 1, status: this.$t('toolStatus.free') },
        { id: 2, status: this.$t('toolStatus.inUse') },
        { id: 3, status: this.$t('toolStatus.broken') },
        { id: 4, status: this.$t('toolStatus.outOfUse') },
        { id: 5, status: this.$t('toolStatus.loaned') },
        { id: 6, status: this.$t('toolStatus.rented') },
        { id: 7, status: this.$t('toolStatus.partlyUsed') },
        { id: 8, status: this.$t('toolStatus.fullyUsed') }
      ];
    },
    filteredTools() {
      if (this.selectedStatusFilter) {
        return this.tools.filter(t => t.toolStatusId === this.selectedStatusFilter);
      }
      return this.tools;
    },
    selectClass() {
      const darkMode = localStorage.getItem('darkMode');
      return darkMode === 'true' ? 'select-primary' : 'select-standard';
    },
    canDeleteAfterMove() {
      return this.moveMode === 'all' && this.reservedCount === 0 && this.tools.length > 0 && !this.loading;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.selectedTargetToolTypeId = null;
        this.moveMode = 'all';
        this.selectedTools = [];
        this.deleteAfterMove = false;
        this.selectedStatusFilter = '';
        this.fetchTools();
      }
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 1024;
    },
    onMoveModeChange(index) {
      this.moveMode = index === 0 ? 'all' : 'selected';
      if (this.moveMode === 'selected') {
        this.deleteAfterMove = false;
      }
    },
    canSelectTool(row) {
      return !row.isReserved;
    },
    handleSelectionChange(selection) {
      this.selectedTools = selection;
    },
    toggleRowSelection(row) {
      if (!this.canSelectTool(row)) return;
      this.$refs.toolTable.toggleRowSelection(row);
    },
    statusTagType(statusId) {
      switch (statusId) {
        case 1: return 'success';
        case 2: return 'danger';
        case 3: return 'warning';
        case 4: return 'info';
        case 5: return 'danger';
        case 6: return 'danger';
        case 7: return 'danger';
        case 8: return 'danger';
        default: return 'info';
      }
    },
    statusLabel(statusId) {
      const map = {
        1: 'toolStatus.free',
        2: 'toolStatus.inUse',
        3: 'toolStatus.broken',
        4: 'toolStatus.outOfUse',
        5: 'toolStatus.loaned',
        6: 'toolStatus.rented',
        7: 'toolStatus.partlyUsed',
        8: 'toolStatus.fullyUsed'
      };
      return this.$t(map[statusId] || 'toolStatus.free');
    },
    async fetchTools() {
      this.loading = true;
      try {
        const response = await api.get(`/ToolType/${this.sourceToolTypeId}/tools`);
        this.tools = response.data;
      } catch (error) {
        console.error('Failed to fetch tools:', error); // eslint-disable-line no-console
        this.tools = [];
      } finally {
        this.loading = false;
      }
    },
    async confirmMove() {
      const toolIds = this.moveMode === 'selected'
        ? this.selectedTools.map(t => t.toolId)
        : null;

      const count = this.moveMode === 'selected' ? this.selectedTools.length : this.movableAllCount;
      const targetName = this.availableTargetToolTypes.find(tt => tt.id === this.selectedTargetToolTypeId)?.type;

      const confirmText = this.deleteAfterMove
        ? this.$t('toolTypes.confirmMoveAndDeleteText', { count, target: targetName })
        : this.$t('toolTypes.confirmMoveText', { count, target: targetName });

      const result = await swal.fire({
        title: this.$t('toolTypes.confirmMoveTitle'),
        text: confirmText,
        icon: 'question',
        showCancelButton: true,
        customClass: {
          confirmButton: this.deleteAfterMove ? 'btn btn-danger btn-fill' : 'btn btn-success btn-fill',
          cancelButton: this.deleteAfterMove ? 'btn btn-outline-secondary' : 'btn btn-danger btn-fill'
        },
        confirmButtonText: this.deleteAfterMove ? this.$t('toolTypes.moveAndDelete') : this.$t('toolTypes.moveTools'),
        cancelButtonText: this.$t('delete.cancel'),
        buttonsStyling: false
      });

      if (!result.value) return;

      try {
        const response = await api.post('/ToolType/move-tools', {
          sourceToolTypeId: this.sourceToolTypeId,
          targetToolTypeId: this.selectedTargetToolTypeId,
          toolIds
        });

        const { movedCount, skippedReservedCount } = response.data;
        const toolTypeNowEmpty = skippedReservedCount === 0 && this.moveMode === 'all';

        let message = this.$t('toolTypes.toolsMovedSuccess', { count: movedCount });
        if (skippedReservedCount > 0) {
          message += ' ' + this.$t('locations.toolsSkippedReserved', { count: skippedReservedCount });
        }

        if (this.deleteAfterMove && toolTypeNowEmpty) {
          await this.deleteSourceToolType();
          message += ' ' + this.$t('toolTypes.toolTypeDeleted');
        }

        swal.fire({
          title: message,
          icon: 'success',
          customClass: { confirmButton: 'btn btn-success btn-fill' },
          buttonsStyling: false
        });

        if (!this.deleteAfterMove && toolTypeNowEmpty) {
          const deleteResult = await swal.fire({
            title: this.$t('toolTypes.toolTypeEmptyTitle'),
            text: this.$t('toolTypes.toolTypeEmptyDeletePrompt', { toolType: this.sourceToolTypeName }),
            icon: 'question',
            showCancelButton: true,
            customClass: {
              confirmButton: 'btn btn-danger btn-fill',
              cancelButton: 'btn btn-success btn-fill'
            },
            confirmButtonText: this.$t('delete.yesDeleteIt'),
            cancelButtonText: this.$t('toolTypes.keepToolType'),
            buttonsStyling: false
          });

          if (deleteResult.value) {
            await this.deleteSourceToolType();
            swal.fire({
              title: `${this.$t('delete.youDeleted')} ${this.sourceToolTypeName}`,
              icon: 'success',
              customClass: { confirmButton: 'btn btn-success btn-fill' },
              buttonsStyling: false
            });
          }
        }

        this.$emit('update:show', false);
        this.$emit('tools-moved');
      } catch (error) {
        console.error('Failed to move tools:', error); // eslint-disable-line no-console
        swal.fire({
          title: this.$t('common.somethingWentWrong'),
          text: this.$t('toolTypes.moveToolsFailed'),
          icon: 'error',
          customClass: { confirmButton: 'btn btn-danger btn-fill' },
          buttonsStyling: false
        });
      }
    },
    async deleteSourceToolType() {
      await api.delete(`/ToolType/${this.sourceToolTypeId}`);
    }
  }
};
</script>

<style scoped>
.modal-scroll-fix {
  overflow-y: auto;
}
.move-tools-modal :deep(.modal-dialog) {
  max-width: 700px;
}
.move-tools-modal :deep(.ts-nav-pills) {
  min-width: 0;
  flex-wrap: nowrap;
}
.move-tools-modal :deep(.ts-nav-pills__item) {
  min-width: 0;
  padding: 6px 8px;
}
.modal-close-icon {
  cursor: pointer;
}
.modal-close-icon .material-icons {
  font-size: 32px;
  color: var(--primary-color);
}
.summary-box {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  100% { transform: rotate(360deg); }
}
</style>
